/**
 * Editing the timer while it runs: the project, task, kind, description and
 * when it started.
 *
 * Shared by the phone's running sheet and the desktop's running panel so the
 * two cannot drift. Picks (project, task, kind) apply at once; the two typed
 * fields are drafts, committed on blur, on close and on Stop — otherwise every
 * keystroke would be a server write, and a half-typed "9:" would move the
 * start to some nonsense time on its way to 9:15.
 */

import { useMemo, useState } from "react";

import { itemProjectId } from "@/components/ItemRow";
import { useTimer } from "@/context/TimerContext";
import { useToast } from "@/context/ToastContext";
import { useAllItems, useAllPhases, useProjects } from "@/hooks/useData";
import { effectiveRate, useClients } from "@/hooks/useTime";
import { defaultKind, fitKind, kindChoices, type EntryKind } from "@/lib/billing";
import { groupTasksByPhase } from "@/lib/taskOptions";

/** "14:05" — what an <input type="time"> holds, in local time. */
function hhmm(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export function useRunningTimerEditor() {
  const timer = useTimer();
  const toast = useToast();
  const { data: projects = [] } = useProjects();
  const { data: clients = [] } = useClients();
  const { data: items = [] } = useAllItems();
  const { data: phases = [] } = useAllPhases();

  const projectId = timer.meta.projectId ?? "";
  const project = projects.find((p) => p.id === projectId);
  const hasClient = !!(effectiveRate(project, clients).clientId || timer.meta.clientId);
  const choices = kindChoices(project, hasClient);
  const kind = fitKind(timer.meta.kind ?? defaultKind(project, hasClient), project, hasClient);
  const taskGroups = useMemo(
    () => groupTasksByPhase(items, phases, projectId || undefined, itemProjectId),
    [items, phases, projectId],
  );
  const task = items.find((i) => i.id === timer.meta.itemId);
  const selectable = projects.filter((p) => p.status !== "archived" && p.kind !== "inbox");

  const [note, setNote] = useState(timer.meta.note ?? "");
  const [startText, setStartText] = useState(hhmm(timer.startedAt));

  /** The typed start as an ISO time on the timer's own day, or null if it
   *  isn't a usable change. Says why when it refuses. */
  function typedStart(): string | null {
    if (!timer.startedAt || startText === hhmm(timer.startedAt)) return null;
    const m = /^(\d{1,2}):(\d{2})$/.exec(startText);
    if (!m) { toast("Enter the start as a time, like 9:15"); return null; }
    const d = new Date(timer.startedAt);
    d.setHours(Number(m[1]), Number(m[2]), 0, 0);
    if (d.getTime() > Date.now()) {
      toast("The start can't be later than now");
      setStartText(hhmm(timer.startedAt));
      return null;
    }
    return d.toISOString();
  }

  function setProject(id: string) {
    const next = projects.find((p) => p.id === id);
    const nextHasClient = !!effectiveRate(next, clients).clientId;
    // A new project brings its own default kind; keeping the old project's
    // could leave Billable on a flat-fee job where it means nothing.
    timer.update({ projectId: id || undefined, itemId: undefined, kind: defaultKind(next, nextHasClient) });
  }

  function setTask(id: string) {
    timer.update({ itemId: id || undefined });
  }

  function setKind(k: EntryKind) {
    timer.update({ kind: k });
  }

  function commitNote() {
    if (note !== (timer.meta.note ?? "")) timer.update({ note });
  }

  function commitStart() {
    const iso = typedStart();
    if (iso) timer.update({ startedAt: iso });
  }

  /** Commit both drafts — for closing the editor. */
  function commit() {
    commitNote();
    commitStart();
  }

  /** Stop and log, with whatever is typed but not yet committed. */
  function stopAndLog() {
    const iso = typedStart();
    void timer.stop({ note, ...(iso ? { startedAt: iso } : {}) });
  }

  return {
    timer, project, task, selectable, taskGroups, kind, choices,
    note, setNote, startText, setStartText,
    setProject, setTask, setKind, commitNote, commitStart, commit, stopAndLog,
  };
}
